import * as THREE from "three";

export interface DroneInput {
  moveX: number;        // strafe left/right: -1..1
  moveZ: number;        // forward/back: -1..1
  moveY: number;        // up/down: -1..1
  lookX: number;        // yaw delta (radians)
  lookY: number;        // pitch delta (radians)
  fast: boolean;
  followToggle: boolean;
  releaseFollow: boolean;  // force free mode (B button)
  nextTarget: boolean;     // cycle forward (RB)
  previousTarget: boolean; // cycle backward (LB)
}

const BASE_SPEED    = 20;
const FAST_MULT     = 3;
const FOLLOW_DIST   = 18;
const FOLLOW_HEIGHT = 10;
const LOOK_AT_Y     = 2.5;
const PITCH_MIN     = -1.2;
const PITCH_MAX     = 0.4;
const FOLLOW_LERP   = 0.001;  // exponential base for smooth follow lerp

export class DroneCameraController {
  position = new THREE.Vector3(0, 15, 0);
  yaw = 0;
  pitch = -0.3;
  followTargetId: string | null = null;

  private smoothPos = new THREE.Vector3(0, 15, 0);
  private prevFollowToggle = false;
  private prevNextTarget = false;
  private prevPrevTarget = false;

  update(
    input: DroneInput,
    getPeer: (id: string) => THREE.Group | null,
    aliveIds: string[],
    dt: number,
  ): void {
    // Apply look
    this.yaw  += input.lookX;
    this.pitch = Math.max(PITCH_MIN, Math.min(PITCH_MAX, this.pitch + input.lookY));

    // Release follow — B button forces free mode
    if (input.releaseFollow) {
      this.followTargetId = null;
    }

    // Follow toggle — rising edge only (A button / keyboard F)
    if (input.followToggle && !this.prevFollowToggle) {
      if (this.followTargetId !== null) {
        this.followTargetId = null;
      } else {
        this.followTargetId = aliveIds[0] ?? null;
      }
    }
    this.prevFollowToggle = input.followToggle;

    // Next/prev target — rising edge; immediately enters follow if not already following
    if (input.nextTarget && !this.prevNextTarget) {
      this.cycleTarget(aliveIds, 1);
    }
    if (input.previousTarget && !this.prevPrevTarget) {
      this.cycleTarget(aliveIds, -1);
    }
    this.prevNextTarget = input.nextTarget;
    this.prevPrevTarget = input.previousTarget;

    if (this.followTargetId !== null) {
      const targetGroup = getPeer(this.followTargetId);
      if (!targetGroup || !targetGroup.visible) {
        this.followTargetId = null;
      } else {
        const tp = targetGroup.position;
        const rotY = targetGroup.rotation.y;
        const sinR = Math.sin(rotY);
        const cosR = Math.cos(rotY);
        const desired = new THREE.Vector3(
          tp.x + sinR * FOLLOW_DIST,
          tp.y + FOLLOW_HEIGHT,
          tp.z + cosR * FOLLOW_DIST,
        );
        const t = 1 - Math.pow(FOLLOW_LERP, dt);
        this.smoothPos.lerp(desired, t);
        this.position.copy(this.smoothPos);
        // Aim at target
        const dx = tp.x - this.position.x;
        const dz = tp.z - this.position.z;
        this.yaw = Math.atan2(dx, dz);
        const dxz = Math.sqrt(dx * dx + dz * dz);
        this.pitch = -Math.atan2((tp.y + LOOK_AT_Y) - this.position.y, dxz);
        return;
      }
    }

    // Free-fly — yaw-only horizontal movement (pitch only affects look, not W/S)
    const speed = (input.fast ? BASE_SPEED * FAST_MULT : BASE_SPEED) * dt;
    const sinY = Math.sin(this.yaw);
    const cosY = Math.cos(this.yaw);
    this.position.x += (sinY * input.moveZ + cosY * input.moveX) * speed;
    this.position.z += (cosY * input.moveZ - sinY * input.moveX) * speed;
    this.position.y += input.moveY * speed;

    // Keep smoothPos in sync so follow re-entry starts from current position
    this.smoothPos.copy(this.position);
  }

  cycleTarget(aliveIds: string[], dir: 1 | -1 = 1): void {
    if (aliveIds.length === 0) {
      this.followTargetId = null;
      return;
    }
    if (this.followTargetId === null) {
      this.followTargetId = dir === 1 ? aliveIds[0] : aliveIds[aliveIds.length - 1];
      return;
    }
    const idx = aliveIds.indexOf(this.followTargetId);
    const next = (idx + dir + aliveIds.length) % aliveIds.length;
    this.followTargetId = aliveIds[next];
  }
}
